import { useTranslation } from 'react-i18next';
import { Eye, Globe, Lock, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SessionNoteStatus } from '@/types/sessions';

/** Status chip for a session note; `null` status means no note yet ("Offen"). */
export function NoteStatusChip({ status, className }: { status: SessionNoteStatus | null; className?: string }) {
  const { t } = useTranslation('sessions');
  const config = {
    open: { label: t('common.statusOpen'), cls: 'bg-warn-soft text-warn-text', Icon: null },
    draft: { label: t('common.statusDraft'), cls: 'bg-brand-50 text-brand-700', Icon: PenLine },
    completed: { label: t('common.statusCompleted'), cls: 'bg-success-soft text-success-text', Icon: Lock },
  }[status ?? 'open'];
  const Icon = config.Icon;
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-xs font-semibold', config.cls, className)}>
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {config.label}
    </span>
  );
}

export function InitialsAvatar({ initials, size = 'md' }: { initials: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizes = { sm: 'h-8 w-8 text-[11px]', md: 'h-10 w-10 text-xs', lg: 'h-14 w-14 text-base' };
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-display font-semibold text-brand-700', sizes[size])}>
      {initials || '·'}
    </span>
  );
}

export function OnlyYouNote({ long = false, icon = 'eye' }: { long?: boolean; icon?: 'eye' | 'lock' }) {
  const { t } = useTranslation('sessions');
  const Icon = icon === 'lock' ? Lock : Eye;
  return (
    <div className="flex items-center gap-2.5 rounded-xl bg-brand-50 px-4 py-3 text-[13px] font-medium text-brand-700">
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{long ? t('common.onlyYouLong') : t('common.onlyYou')}</span>
    </div>
  );
}

/** The three privacy claims from the landing page. Exactly these, never stronger. */
export function PrivacyClaims() {
  const { t } = useTranslation('sessions');
  const claims = [
    { Icon: Lock, text: t('empty.claim1') },
    { Icon: Eye, text: t('empty.claim2') },
    { Icon: Globe, text: t('empty.claim3') },
  ];
  return (
    <ul className="m-0 list-none rounded-2xl border border-line bg-surface px-5 py-1.5 text-left">
      {claims.map(({ Icon, text }, i) => (
        <li key={i} className={cn('flex items-start gap-3 py-3 text-[14.5px] font-medium leading-relaxed text-ink', i > 0 && 'border-t border-line')}>
          <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-brand-500" aria-hidden="true" />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}
